import * as React from "react";
import {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { IFormThemeValues } from "./pages/config/BrandingPage";
import { axios, withRetry } from "./AxiosInstanceManager";
import { DisplaySettings } from "../dto/DisplaySettings";
import { PacSettings } from "../dto/PacSettings";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";
import { createAsyncCache } from "../util/createAsyncCache";

export interface LocationConfigurationAndHashCode {
  locationConfiguration: LocationConfiguration | null;
  hashCode: number;
}

interface LocationConfigurationUpdate {
  locationConfiguration: LocationConfiguration;
  updated: number;
}

export const LocationConfigurationHolder = (() => {
  let config: string = window.config || "pac";
  let hashCode: number | null = null;
  let lastNotified: number | null = null;
  const subscribers: ((event: LocationConfigurationUpdate) => void)[] = [];

  const fetchLocationConfigurationInternal = async () => {
    const { data } = await withRetry<LocationConfigurationAndHashCode>(() =>
      axios.post<LocationConfigurationAndHashCode>(
        "/locationConfiguration/getIfChanged",
        { config, hashCode },
      ),
    );

    hashCode = data.hashCode;

    // this should always have a value on initial fetch
    if (data.locationConfiguration) {
      return {
        locationConfiguration: data.locationConfiguration,
        updated: Date.now(),
      };
    }

    if (!locationConfigurationCache.current) {
      throw new Error("Location configuration cache not initialized");
    }

    return locationConfigurationCache.current;
  };

  const locationConfigurationCache = createAsyncCache<{
    locationConfiguration: LocationConfiguration;
    updated: number;
  }>(fetchLocationConfigurationInternal);

  const notifySubscribers = () => {
    const currentValue = locationConfigurationCache.current;
    if (!currentValue) return;
    lastNotified = currentValue.updated;
    subscribers.forEach((subscriber) => subscriber(currentValue));
  };

  const fetchLocationConfiguration = async (force: boolean = false) => {
    const { locationConfiguration, updated } =
      await locationConfigurationCache.get(force);
    if (!lastNotified || lastNotified < updated) {
      notifySubscribers();
    }
    return locationConfiguration;
  };

  /**
   * used to refresh and force notify subscribers after saving changes to the location config
   */
  const reloadLocationConfiguration = async () => {
    // clear the hash code so that the next fetch will request fresh data from the server
    hashCode = null;
    const { locationConfiguration } = await locationConfigurationCache.get(
      true,
    );
    notifySubscribers(); // force notify subscribers
    return locationConfiguration;
  };

  const subscribe = (
    subscriber: (event: LocationConfigurationUpdate) => void,
    onError?: (error: unknown) => void,
  ) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);

    if (locationConfigurationCache.current) {
      subscriber(locationConfigurationCache.current);
    } else {
      // trigger a fetch which will notify the subscriber when it completes
      void fetchLocationConfiguration().catch((error) => {
        console.error(
          "Failed to fetch location configuration on subscribe",
          error,
        );
        if (onError) {
          onError(error);
        }
      });
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  return {
    subscribe,
    reloadLocationConfiguration,
    fetchLocationConfiguration,
    get current() {
      return locationConfigurationCache.current?.locationConfiguration || null;
    },
    get lastUpdated() {
      return locationConfigurationCache.current?.updated;
    },
  };
})();

export interface LocationConfiguration {
  id: number;
  name: string;
  isDefault: boolean;
  libraryName: string;
  helpSettings: HelpSettings;
  pacSettings: PacSettings;
  filterBranches: BranchView[];
}

export interface BranchView {
  id: number;
  displayName: string;
  longDescription: string;
  identifier: string;
  /**
   * this is the same value as BranchLimit.hidden
   */
  shared: boolean;
}

export interface Branch {
  id: number;
  displayName: string;
  longDescription: string;
  identifier: string;
  shared: boolean;
  displayInCopies: boolean;
  displayInBranchLimits: boolean;
  displayInPickupLocation: boolean;
}

export interface HelpSettings {
  helpURLBase: string;
  localHelpURL: string;
}

export enum AppBarColor {
  Default = "Default",
  Primary = "Primary",
  Secondary = "Secondary",
}

export enum HeaderPosition {
  Top = "Top",
  Bottom = "Bottom",
}

export enum SearchResultsViewType {
  listview = "listview",
  gridview = "gridview",
}

export enum TitleTab {
  Details = "Details",
  AllCopies = "All Copies",
  Reviews = "Reviews",
  Related = "Related",
  MoreInfo = "More Info",
}

export interface Field {
  id: number;
  tag: number;
  subfields: string;
}

export interface LocationConfigurationContextType {
  locationConfiguration: LocationConfiguration | null;
  saveTheme: (values: IFormThemeValues) => Promise<boolean>;
}

interface ConfigurationGroup {
  id: number;
  name: string;
  isUserDefined: boolean;
  locationConfigurationIds: number[];
}

interface LocationConfigurationInfo {
  id: number;
  libraryName: string;
  name: string;
  isDefault: boolean;
  configurationGroup: ConfigurationGroup[];
  filterBranches: Branch[];
  updateCollectionFilters: boolean;
}

interface LocationConfigurationManagerStatus {
  success: boolean;
  message: string;
  locationConfigurationInfo: LocationConfigurationInfo;
}

export const LocationConfigurationContext =
  createContext<LocationConfigurationContextType | null>(null);

export const LocationConfigurationProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { handleRequestError } = useErrorDialog();
  const [locationConfiguration, setLocationConfiguration] =
    useState<LocationConfiguration | null>(LocationConfigurationHolder.current);
  const lastUpdated = useRef<number>(LocationConfigurationHolder.lastUpdated);

  useEffect(() => {
    let active = true;
    let timeoutId: number | undefined;
    const updateFrequency = 5 * 60 * 1000; //5 minutes

    const poll = async () => {
      try {
        const data =
          await LocationConfigurationHolder.fetchLocationConfiguration(true);
        if (!active) return;
        if (
          !lastUpdated.current ||
          LocationConfigurationHolder.lastUpdated > lastUpdated.current
        ) {
          lastUpdated.current = LocationConfigurationHolder.lastUpdated;
          setLocationConfiguration(data);
        }
      } catch (e) {
        if (active) {
          if (!LocationConfigurationHolder.current) {
            // only show the error dialog if we don't have a valid configuration to fall back on, otherwise just wait for the next poll to hopefully succeed
            handleRequestError(e);
          }
        }
      } finally {
        if (active) {
          timeoutId = window.setTimeout(poll, updateFrequency);
        }
      }
    };

    if (LocationConfigurationHolder.current) {
      timeoutId = window.setTimeout(poll, updateFrequency);
    } else {
      // if we don't have a pre-fetched value, trigger the fetch immediately
      poll();
    }

    return () => {
      active = false;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [handleRequestError]);

  const saveTheme = useCallback(
    async ({
      primaryColor,
      secondaryColor,
      appBarColor,
      mainPageLogoImageId,
      mainPageLogoImageAltText,
      libraryName,
      headerPosition,
      showHeaderOnMobile,
    }: IFormThemeValues) => {
      if (!locationConfiguration) {
        console.error("No location configuration available to save theme");
        return false;
      }
      // Because save config expect an admin view of the location configuration we need to request it
      try {
        const adminLocationConfigurationData = (
          await axios.get<LocationConfiguration>(
            `/admin/locationConfiguration/${locationConfiguration.id}`,
          )
        ).data;

        const displaySettings: DisplaySettings = {
          ...adminLocationConfigurationData.pacSettings.displaySettings,
          primaryColor,
          secondaryColor,
          appBarColor,
          mainPageLogoImageId,
          mainPageLogoImageAltText,
          headerPosition,
          showHeaderOnMobile,
        };

        const pacSettings = {
          ...adminLocationConfigurationData.pacSettings,
          displaySettings,
        };
        const payload = {
          ...adminLocationConfigurationData,
          libraryName,
          pacSettings,
        };
        try {
          const { data } = await axios.put<
            LocationConfigurationManagerStatus[]
          >(
            `/admin/locationConfiguration/${locationConfiguration.id}`,
            payload,
          );

          // There should only be one LocationConfigurationManagerStatus
          const response = data[0];
          if (response.success === true) {
            try {
              const data =
                await LocationConfigurationHolder.reloadLocationConfiguration();
              setLocationConfiguration(data);
            } catch (e) {
              console.error("Failed to reload location configuration", e);
              handleRequestError(e);
            } finally {
              // even if reloading the configuration fails, we still want to return true here since the save itself was successful
              return true;
            }
          }
          console.error(response.message);
          return false;
        } catch (e) {
          console.log(e);
          // fallback to the view's error handling
          return false;
        }
      } catch (e) {
        console.error("Failed to fetch admin location configuration", e);
        handleRequestError(e);
      }
    },
    [locationConfiguration, handleRequestError],
  );

  const contextValue = React.useMemo(
    () => ({
      locationConfiguration,
      saveTheme,
    }),
    [locationConfiguration, saveTheme],
  );

  return (
    <LocationConfigurationContext.Provider value={contextValue}>
      {children}
    </LocationConfigurationContext.Provider>
  );
};

export function useLocationConfiguration() {
  const context = useContext(LocationConfigurationContext);

  if (!context) {
    throw new Error(
      "useLocationConfiguration must be used within a LocationConfigurationProvider",
    );
  }

  return context;
}
